// src/App.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./index.css";

import Header from "./components/Header";
import Footer from "./components/Footer";
import AboutModal from "./components/AboutModal";

/* Apple badge */
function AppStoreBadge({ href = "https://apps.apple.com/app/id6755364999" }) {
    return (
        <a className="store-badge appstore" href={href} aria-label="Download on the App Store">
            <span className="icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M16.365 12.96c.03 3.273 2.875 4.364 2.905 4.377-.023.074-.455 1.54-1.503 3.05-.905 1.31-1.846 2.62-3.33 2.646-1.46.026-1.926-.85-3.592-.85-1.667 0-2.192.823-3.576.877-1.438.054-2.527-1.414-3.44-2.718-1.87-2.676-3.3-7.56-1.379-10.864.952-1.657 2.653-2.71 4.513-2.737 1.409-.027 2.738.934 3.593.934.856 0 2.476-1.15 4.173-.982.711.03 2.714.278 3.99 2.098-.104.065-2.387 1.393-2.254 3.168zM13.8 3.747c.778-.942 1.303-2.256 1.16-3.557-1.122.046-2.485.746-3.289 1.688-.724.834-1.36 2.172-1.19 3.455 1.26.098 2.54-.64 3.319-1.586z"/>
                </svg>
            </span>
            <span className="txt">
                <em>Download on the</em>
                <strong>App Store</strong>
            </span>
        </a>
    );
}

/* Google Play badge */
function GooglePlayBadge({ href = "https://play.google.com/store/apps/details?id=com.marikag.ninoaspace" }) {
    return (
        <a className="store-badge play" href={href}>
            <span className="icon">
                <svg viewBox="0 0 24 24">
                    <path className="c1" d="M3 2l10.5 10.5L3 23z" />
                    <path className="c2" d="M13.5 12.5L17.8 16 6 23z" />
                    <path className="c3" d="M17.8 16l3.2-1.8..." />
                    <path className="c4" d="M13.5 11.5L6 2l11.8 6z" />
                </svg>
            </span>
            <span className="txt">
                <em>GET IT ON</em>
                <strong>Google Play</strong>
            </span>
        </a>
    );
}

/* SVG Icons for feature cards */
function ClipboardIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
            <path d="M9 14l2 2 4-4"/>
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
        </svg>
    );
}

function FileTextIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
        </svg>
    );
}

function LockIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
    );
}

function ScanIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V5a2 2 0 012-2h2"/>
            <path d="M17 3h2a2 2 0 012 2v2"/>
            <path d="M21 17v2a2 2 0 01-2 2h-2"/>
            <path d="M7 21H5a2 2 0 01-2-2v-2"/>
            <circle cx="12" cy="12" r="3"/>
            <path d="M3 12h2M19 12h2M12 3v2M12 19v2"/>
        </svg>
    );
}

function CompareIcon() {
    return (
        <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="7" height="16" rx="1.5"/>
            <rect x="14" y="4" width="7" height="16" rx="1.5"/>
            <path d="M6.5 12h0M17.5 12h0"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

/* Feather-style SVG icons for blobs */
const BLOB_ICONS = {
    insights: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    learn: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
    challenge: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    tracking: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    weather: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></svg>,
    connections: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D2B42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
};

const BLOB_FEATURES = [
    { icon: "insights", labelKey: "home.blob.insights", delay: 0 },
    { icon: "learn", labelKey: "home.blob.learning", delay: 1 },
    { icon: "challenge", labelKey: "home.blob.challenges", delay: 2 },
    { icon: "tracking", labelKey: "home.blob.tracking", delay: 3 },
    { icon: "weather", labelKey: "home.blob.weather", delay: 4 },
    { icon: "connections", labelKey: "home.blob.social", delay: 5 },
];

/* Condition cards: slug + icon; names and descriptions come from translations */
const CONDITION_CARDS = [
    {
        slug: "psoriasis",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12s1.5-2 4-2 4 2 4 2"/><path d="M8 16s1.5-2 4-2 4 2 4 2"/></svg>,
    },
    {
        slug: "eczema",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 9h.01M15 9h.01M9 15c1.5 1 4.5 1 6 0"/></svg>,
    },
    {
        slug: "rosacea",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>,
    },
    {
        slug: "acne",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="8" cy="10" r="1.5"/><circle cx="16" cy="10" r="1.5"/><circle cx="12" cy="14" r="1.5"/></svg>,
    },
    {
        slug: "urticaria",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 11c0-2 1.5-3 4-3s4 1 4 3"/><path d="M8 15c0-2 1.5-3 4-3s4 1 4 3"/></svg>,
    },
    {
        slug: "hidradenitis",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 12a3 3 0 016 0"/><line x1="12" y1="8" x2="12" y2="8.01"/></svg>,
    },
    {
        slug: "vitiligo",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 8c2 0 3 2 4 4s2 4 4 4"/></svg>,
    },
    {
        slug: "alopecia",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6c-2 0-3 1.5-3 3s1.5 2 3 2 3-.5 3-2"/><path d="M9 16c1 1 2 2 3 2s2-1 3-2"/></svg>,
    },
    {
        slug: "perioral-dermatitis",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><path d="M12 9v-.01M15 12h.01M12 15v.01M9 12h-.01"/></svg>,
    },
    {
        slug: "seborrheic-dermatitis",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M7 13s2-1 5-1 5 1 5 1"/><path d="M7 9s2-1 5-1 5 1 5 1"/></svg>,
    },
    {
        slug: "prurigo-nodularis",
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="9" cy="10" r="1.6"/><circle cx="15.5" cy="13.5" r="1.6"/><circle cx="10" cy="15.5" r="1.2"/></svg>,
    },
];

/* Translation keys use the slug with dashes replaced (perioral-dermatitis -> perioral_dermatitis) */
const condKey = (slug) => slug.replace(/-/g, "_");

// ------------------------------
// HOME PAGE
// ------------------------------
function Home({ onConditionClick }) {
    const { t } = useTranslation();

    useEffect(() => {
        const sections = [...document.querySelectorAll("main, section[id]")];
        const links = [...document.querySelectorAll("header nav a, header .nav-link")];

        const io = new IntersectionObserver((entries) => {
            const top = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

            if (!top) return;

            const id = top.target.getAttribute("id") || "top";

            links.forEach((a) => {
                const target = a.getAttribute("href")?.replace("#", "");
                a.setAttribute("aria-current", target === id ? "page" : "");
            });
        }, {
            rootMargin: "-40% 0px -55% 0px",
            threshold: [0, 0.25, 0.5, 0.75, 1]
        });

        sections.forEach((s) => io.observe(s));
        return () => io.disconnect();
    }, []);

    return (
        <>
            {/* ==================== HERO ==================== */}
            <main id="top" className="hero">
                <div className="container hero-container">
                    <h1 className="hero-tagline fade">{t("home.hero.title")}</h1>

                    <p className="hero-subtitle fade">
                        {t("home.hero.subtitle")}
                    </p>

                    <h2 className="hero-conditions fade">
                        {t("home.hero.conditions")}
                    </h2>

                    {/* Floating blobs scattered across hero */}
                    <div className="floating-blobs">
                        {BLOB_FEATURES.map((feat, i) => (
                            <div
                                key={feat.labelKey}
                                className={`floating-blob blob-${i}`}
                                style={{ animationDelay: `${i * 0.8}s` }}
                            >
                                <div className="blob-shape">
                                    <span className="blob-icon">{BLOB_ICONS[feat.icon]}</span>
                                </div>
                                <span className="blob-label">{t(feat.labelKey)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="actions fade store-badges">
                        <AppStoreBadge />
                        <GooglePlayBadge />
                    </div>

                    <p className="hero-trial fade">{t("home.hero.trial")}</p>

                    <div className="trust fade">
                        <span className="pill">{t("home.trust.no_ads")}</span>
                        <span className="pill">{t("home.trust.no_sale")}</span>
                        <span className="pill">{t("home.trust.gdpr")}</span>
                    </div>
                </div>
            </main>

            {/* ==================== FEATURES ==================== */}
            <section id="features" className="container">
                <h2 className="section-title fade">{t("home.features.title")}</h2>
                <div className="grid cols-3">
                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <ScanIcon />
                        </div>
                        <h3>{t("home.features.scanner.title")}</h3>
                        <p>{t("home.features.scanner.desc")}</p>
                    </article>

                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <CompareIcon />
                        </div>
                        <h3>{t("home.features.compare.title")}</h3>
                        <p>{t("home.features.compare.desc")}</p>
                    </article>

                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <ShieldIcon />
                        </div>
                        <h3>{t("home.features.flare.title")}</h3>
                        <p>{t("home.features.flare.desc")}</p>
                    </article>

                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <ClipboardIcon />
                        </div>
                        <h3>{t("home.features.log.title")}</h3>
                        <p>{t("home.features.log.desc")}</p>
                    </article>

                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <FileTextIcon />
                        </div>
                        <h3>{t("home.features.reports.title")}</h3>
                        <p>{t("home.features.reports.desc")}</p>
                    </article>

                    <article className="feature-card fade">
                        <div className="feature-icon-wrap">
                            <LockIcon />
                        </div>
                        <h3>{t("home.features.privacy.title")}</h3>
                        <p>{t("home.features.privacy.desc")}</p>
                    </article>
                </div>
            </section>

            {/* ==================== HOW IT WORKS ==================== */}
            <section id="how" className="container">
                <h2 className="section-title fade">{t("home.how.title")}</h2>
                <div className="steps-row">
                    <StepCard n={1} title={t("home.how.step1.title")} desc={t("home.how.step1.desc")} />
                    <div className="step-connector" />
                    <StepCard n={2} title={t("home.how.step2.title")} desc={t("home.how.step2.desc")} />
                    <div className="step-connector" />
                    <StepCard n={3} title={t("home.how.step3.title")} desc={t("home.how.step3.desc")} />
                </div>
            </section>

            {/* ==================== CONDITIONS ==================== */}
            <section id="conditions" className="container">
                <h2 className="section-title fade">{t("home.conditions.title")}</h2>
                <div className="grid cols-3">
                    {CONDITION_CARDS.map((card) => (
                        <div key={card.slug} className="condition-card fade" onClick={() => onConditionClick(card.slug)}>
                            <div className="condition-icon">
                                {card.icon}
                            </div>
                            <h3>{t(`home.conditions.${condKey(card.slug)}.title`)}</h3>
                            <p>{t(`home.conditions.${condKey(card.slug)}.desc`)}</p>
                            <span className="condition-learn-more">{t("home.conditions.learn_more")}</span>
                        </div>
                    ))}
                </div>
                <p className="conditions-more fade">{t("home.conditions.more")}</p>
            </section>

            {/* ==================== PRICING ==================== */}
            <section id="pricing" className="container">
                <h2 className="section-title fade">{t("home.pricing.title")}</h2>
                <p className="section-subtitle fade">{t("home.pricing.subtitle")}</p>

                <div className="pricing-grid fade">
                    <div className="pricing-card pricing-card--tier">
                        <h3 className="pricing-tier-name">{t("home.pricing.free.name")}</h3>
                        <p className="pricing-tier-tagline">{t("home.pricing.free.tagline")}</p>
                        <div className="pricing-amount">
                            <span className="pricing-number pricing-number--free">{t("home.pricing.free.price")}</span>
                        </div>
                        <p className="pricing-annual">{t("home.pricing.free.nocard")}</p>

                        <ul className="pricing-features">
                            <li><CheckIcon /> {t("home.pricing.free.f1")}</li>
                            <li><CheckIcon /> {t("home.pricing.free.f2")}</li>
                            <li><CheckIcon /> {t("home.pricing.free.f3")}</li>
                            <li><CheckIcon /> {t("home.pricing.free.f4")}</li>
                            <li><CheckIcon /> {t("home.pricing.free.f5")}</li>
                            <li><CheckIcon /> {t("home.pricing.free.f6")}</li>
                        </ul>
                    </div>

                    <div className="pricing-card pricing-card--tier">
                        <h3 className="pricing-tier-name">{t("home.pricing.basic.name")}</h3>
                        <p className="pricing-tier-tagline">{t("home.pricing.basic.tagline")}</p>
                        <div className="pricing-amount">
                            <span className="pricing-currency">&#8364;</span>
                            <span className="pricing-number">6.99</span>
                            <span className="pricing-period">{t("home.pricing.per_month")}</span>
                        </div>
                        <p className="pricing-annual">{t("home.pricing.basic.annual")}</p>

                        <ul className="pricing-features">
                            <li><CheckIcon /> {t("home.pricing.basic.f1")}</li>
                            <li><CheckIcon /> {t("home.pricing.basic.f2")}</li>
                            <li><CheckIcon /> {t("home.pricing.basic.f3")}</li>
                            <li><CheckIcon /> {t("home.pricing.basic.f4")}</li>
                            <li><CheckIcon /> {t("home.pricing.basic.f5")}</li>
                        </ul>
                    </div>

                    <div className="pricing-card pricing-card--tier pricing-card--featured">
                        <div className="pricing-badge">{t("home.pricing.premium.badge")}</div>
                        <h3 className="pricing-tier-name">{t("home.pricing.premium.name")}</h3>
                        <p className="pricing-tier-tagline">{t("home.pricing.premium.tagline")}</p>
                        <div className="pricing-amount">
                            <span className="pricing-currency">&#8364;</span>
                            <span className="pricing-number">10.99</span>
                            <span className="pricing-period">{t("home.pricing.per_month")}</span>
                        </div>
                        <p className="pricing-annual">{t("home.pricing.premium.annual")}</p>

                        <ul className="pricing-features">
                            <li><CheckIcon /> {t("home.pricing.premium.f1")}</li>
                            <li><CheckIcon /> <strong>{t("home.pricing.premium.f2")}</strong></li>
                            <li><CheckIcon /> <strong>{t("home.pricing.premium.f3")}</strong></li>
                        </ul>
                    </div>
                </div>

                <div className="pricing-cta-row fade">
                    <AppStoreBadge />
                    <GooglePlayBadge />
                </div>
            </section>

            {/* ==================== DATA PRIVACY ==================== */}
            <section id="data" className="container">
                <h2 className="section-title fade">{t("home.data.title")}</h2>
                <div className="grid cols-2">
                    <div className="privacy-card fade">
                        <div className="privacy-icon-wrap">
                            <LockIcon />
                        </div>
                        <h3>{t("home.data.privacy.title")}</h3>
                        <p>{t("home.data.privacy.desc")}</p>
                    </div>
                    <div className="privacy-card fade">
                        <div className="privacy-icon-wrap">
                            <ShieldIcon />
                        </div>
                        <h3>{t("home.data.control.title")}</h3>
                        <p>{t("home.data.control.desc")}</p>
                    </div>
                </div>
            </section>

            {/* ==================== STORY ==================== */}
            <section id="story" className="container">
                <div className="story-card fade">
                    <h2>{t("home.story.title")}</h2>
                    <p className="story-text">
                        {t("home.story.p1")}
                    </p>
                    <p className="story-text">
                        {t("home.story.p2")}
                    </p>
                    <p className="story-signature">{t("home.story.signature")}</p>
                </div>
            </section>

        </>
    );
}

function StepCard({ n, title, desc }) {
    return (
        <div className="step-card fade">
            <div className="step-number">{n}</div>
            <h3>{title}</h3>
            <p>{desc}</p>
        </div>
    );
}

// ------------------------------
// MAIN APP WRAPPER
// ------------------------------
export default function App() {
    const [showAbout, setShowAbout] = useState(false);
    const navigate = useNavigate();

    const handleConditionClick = (slug) => {
        navigate(`/${slug}`);
    };

    return (
        <>
            <Header
                openAbout={() => setShowAbout(true)}
            />

            <Home onConditionClick={handleConditionClick} />

            <Footer openAbout={() => setShowAbout(true)} />

            <AboutModal
                isOpen={showAbout}
                onClose={() => setShowAbout(false)}
            />
        </>
    );
}
